import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { PetpoojaConnectPanel } from '../components/PetpoojaConnectPanel';
import {
  ShoppingBag, Upload, FileSpreadsheet, ArrowRight, CheckCircle2,
  AlertCircle, Sparkles, Zap, Globe, FlaskConical, Loader2, X, Plug,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Friendly label for module keys returned by the classifier
const MODULE_LABELS = {
  revenue: 'Revenue / Daily Sales',
  expenses: 'Expenses',
  suppliers: 'Supplier Invoices',
  reconciliation: 'Bank Reconciliation',
  recipes: 'Recipes',
  menu: 'Menu / Price List',
  menu_performance: 'Menu Performance (Sales Mix)',
  mandi: 'Mandi Prices',
  reviews: 'Customer Reviews',
};

// Real-world POS systems with their actual report export paths
const POS_SYSTEMS = [
  {
    key: 'petpooja',
    name: 'Petpooja',
    region: 'India · #1 small/mid restaurants',
    color: 'bg-orange-50 text-orange-900 border-orange-200',
    reports: [
      { name: 'Daily Sales Report', path: 'Reports → Sales → Daily Sales → Export Excel', maps: 'Revenue' },
      { name: 'Item-wise Sales',    path: 'Reports → Sales → Item Sales → Export Excel',  maps: 'Menu Performance' },
      { name: 'Bill-wise Sales',    path: 'Reports → Sales → Bill Wise → Export Excel',   maps: 'Revenue (with Z-ref)' },
      { name: 'Payment Mode Report', path: 'Reports → Payment Mode → Export',             maps: 'Reconciliation (Card vs Bill)' },
      { name: 'Tax Report (GST)',   path: 'Reports → Tax Report → Export',                maps: 'Revenue (taxes)' },
    ],
  },
  {
    key: 'posist',
    name: 'Posist',
    region: 'India · Chains, premium',
    color: 'bg-violet-50 text-violet-900 border-violet-200',
    reports: [
      { name: 'Day-Close Summary',    path: 'Dashboard → Reports → Day Close → Download', maps: 'Revenue + Reconciliation' },
      { name: 'Item-wise Sales Mix',  path: 'Reports → Sales → Item Mix → Export Excel',  maps: 'Menu Performance' },
      { name: 'Discount / Void',      path: 'Reports → Discounts → Export',               maps: 'Expenses (write-offs)' },
      { name: 'Tax Summary',          path: 'Reports → Tax → Export',                     maps: 'Revenue (taxes)' },
    ],
  },
  {
    key: 'limetray',
    name: 'Limetray / InResto',
    region: 'India · Mid-tier',
    color: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    reports: [
      { name: 'Daily Z-Close',        path: 'Reports → Daily Z-Close → Download', maps: 'Revenue' },
      { name: 'Item Sales',           path: 'Reports → Item Sales → Excel',       maps: 'Menu Performance' },
      { name: 'Payment Modes',        path: 'Reports → Payments → Excel',         maps: 'Reconciliation' },
    ],
  },
  {
    key: 'toast',
    name: 'Toast',
    region: 'US · #1 mid-market',
    color: 'bg-rose-50 text-rose-900 border-rose-200',
    reports: [
      { name: 'Daily Sales Summary',   path: 'Reporting → Daily Sales Summary → Export CSV', maps: 'Revenue' },
      { name: 'Menu Item Sales',       path: 'Reporting → Sales → Menu Items → Export',      maps: 'Menu Performance' },
      { name: 'Tender Reconciliation', path: 'Reporting → Tender Recon → Export',            maps: 'Reconciliation' },
    ],
  },
  {
    key: 'square',
    name: 'Square for Restaurants',
    region: 'US/Global · Small business',
    color: 'bg-blue-50 text-blue-900 border-blue-200',
    reports: [
      { name: 'Sales Summary',     path: 'Dashboard → Reports → Sales Summary → Export CSV', maps: 'Revenue' },
      { name: 'Item Sales',        path: 'Reports → Item Sales → Export CSV',                maps: 'Menu Performance' },
      { name: 'Payment Methods',   path: 'Reports → Payment Methods → Export',               maps: 'Reconciliation' },
    ],
  },
  {
    key: 'micros_symphony',
    name: 'Oracle Micros Symphony',
    region: 'Global · Enterprise hotels & chains',
    color: 'bg-red-50 text-red-900 border-red-200',
    reports: [
      { name: 'Daily Operations Report',  path: 'Reporting & Analytics → Daily Operations → Export Excel',  maps: 'Revenue' },
      { name: 'Menu Item Sales',          path: 'Reporting & Analytics → Sales → Menu Item Sales → Export', maps: 'Menu Performance' },
      { name: 'Tender Media Summary',     path: 'Reporting & Analytics → Tender Media → Export',            maps: 'Reconciliation (Card vs Bill)' },
      { name: 'Check Detail Report',      path: 'Reporting & Analytics → Check Detail → Export',            maps: 'Revenue (with check seq)' },
      { name: 'Major/Family Group Sales', path: 'Reporting & Analytics → Group Sales → Export',             maps: 'Menu Performance (by group)' },
    ],
  },
  {
    key: 'oracle_opera',
    name: 'Oracle Opera PMS',
    region: 'Global · Hotels (PMS + F&B)',
    color: 'bg-indigo-50 text-indigo-900 border-indigo-200',
    reports: [
      { name: 'Night Audit / Trial Balance', path: 'Reports → Financials → Trial Balance → Export Excel', maps: 'Revenue (daily F&B)' },
      { name: 'F&B Revenue Report',          path: 'Reports → F&B → Revenue Report → Export',             maps: 'Revenue' },
      { name: 'Outlet Sales by Article',     path: 'Reports → F&B → Outlet Sales → Export Excel',          maps: 'Menu Performance' },
      { name: 'Cashier Closing Report',      path: 'Reports → Cashiering → Closing Report → Export',       maps: 'Reconciliation' },
      { name: 'Tax Report (GST/VAT)',        path: 'Reports → Financials → Tax Report → Export',           maps: 'Revenue (taxes)' },
    ],
  },
  {
    key: 'lightspeed',
    name: 'Lightspeed Restaurant',
    region: 'Global',
    color: 'bg-slate-50 text-slate-900 border-slate-200',
    reports: [
      { name: 'Daily Sales',       path: 'Reports → Daily Sales → Export Excel', maps: 'Revenue' },
      { name: 'Items Sold',        path: 'Reports → Sales → Items → Excel',      maps: 'Menu Performance' },
      { name: 'Payment Types',     path: 'Reports → Payments → Export',          maps: 'Reconciliation' },
    ],
  },
  {
    key: 'restaurant365',
    name: 'Restaurant365',
    region: 'US enterprise',
    color: 'bg-cyan-50 text-cyan-900 border-cyan-200',
    reports: [
      { name: 'Daily Sales & Labor',   path: 'Reports → Daily Sales → Export', maps: 'Revenue + Expenses' },
      { name: 'Menu Item Performance', path: 'Reports → Menu → Export',        maps: 'Menu Performance' },
    ],
  },
  {
    key: 'touchbistro',
    name: 'TouchBistro',
    region: 'Canada/US · Independents',
    color: 'bg-amber-50 text-amber-900 border-amber-200',
    reports: [
      { name: 'End-of-Day Report', path: 'Reports → EOD → Email/Export', maps: 'Revenue' },
      { name: 'Sales by Item',     path: 'Reports → Sales → Items → Export', maps: 'Menu Performance' },
    ],
  },
];

export const POSIntegrations = () => {
  const { selectedClient } = useClient();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  if (!selectedClient) {
    return <div className="text-center text-text-secondary py-12" data-testid="pos-no-client">Select a client.</div>;
  }

  return (
    <div className="space-y-6" data-testid="pos-integrations-page">
      {/* Header */}
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">POS Integrations</p>
        <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
          <ShoppingBag className="h-7 w-7" /> Connect Your POS — {selectedClient.name}
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Two ways to sync — direct API (set & forget) for supported POSes, or daily report export for everything else.
        </p>
      </div>

      {/* === Direct API connection (Petpooja) === */}
      <section className="bg-white border border-border rounded-lg p-5 shadow-sm" data-testid="petpooja-direct-section">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-orange-700 font-bold mb-1">
              Recommended · Direct API
            </div>
            <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
              <Plug className="h-5 w-5 text-orange-600" /> Petpooja — auto-sync
            </h2>
            <p className="text-xs text-text-secondary mt-1">
              Sales, inventory, menu, and tax data pulled automatically every night. No exports, no uploads.
            </p>
          </div>
          <div className="hidden sm:flex flex-col items-end text-xs text-text-muted">
            <span>Coming soon for Posist · Restroworks · Toast · Square</span>
          </div>
        </div>
        <PetpoojaConnectPanel client={selectedClient} isAdmin={isAdmin} />
      </section>

      {/* === Fallback: export → upload === */}
      <section data-testid="pos-export-section">
        <div className="mb-3 pb-2 border-b border-border">
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-muted font-bold mb-1">
            Fallback · Daily Export → Upload
          </div>
          <h2 className="text-lg font-semibold text-primary">Don't have API access yet? Export & drop</h2>
          <p className="text-xs text-text-secondary mt-1">
            Works for any POS — even ones we don't have direct API support for yet.
          </p>
        </div>
        {/* How it works */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5" data-testid="pos-how-it-works">
          <Step n={1} icon={<FileSpreadsheet className="h-5 w-5" />} title="Export from your POS">
            Open the report card below for your POS — follow the menu path to download an Excel/CSV.
          </Step>
          <Step n={2} icon={<Upload className="h-5 w-5" />} title="Drop in Files">
            Drag the file into our Smart Upload Zone. <Link to="/files" className="text-primary hover:underline font-semibold">Go to Files →</Link>
          </Step>
          <Step n={3} icon={<Sparkles className="h-5 w-5" />} title="AI does the rest">
            Our Gemini 3 extractor recognises your POS's column names, normalises everything, and routes to the right module.
          </Step>
        </div>
        <TestParseWidget />
      </section>

      {/* POS grid */}
      <div>
        <h2 className="text-base font-semibold text-primary mb-3">Supported POS systems</h2>
        <p className="text-xs text-text-muted mb-4">
          The AI extractor is taught the column synonyms each POS uses (Net Sales / Gross Sales / Total Sales / Total Bill / etc.).
          <strong> Any POS not listed below still works</strong> — just upload the file, the AI figures out the layout.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {POS_SYSTEMS.map(pos => (
            <PosCard key={pos.key} pos={pos} />
          ))}
        </div>
      </div>

      {/* Tier 2/3 callout */}
      <div className="bg-slate-50 border border-slate-200 text-slate-800 rounded-md p-4" data-testid="pos-future-tiers">
        <div className="flex items-start gap-3">
          <Zap className="h-5 w-5 mt-0.5 flex-shrink-0 text-primary" />
          <div>
            <h3 className="text-sm font-semibold mb-1">Coming next</h3>
            <ul className="text-xs space-y-1 list-disc list-inside text-text-secondary">
              <li><strong>Email forwarding</strong> — set your POS's daily-report email to <code className="bg-slate-100 px-1 rounded">reports+&lt;client_id&gt;@epicureanexus.com</code> and we'll auto-ingest every night.</li>
              <li><strong>Direct API sync (Posist, Toast, Square)</strong> — same set-and-forget experience as Petpooja, for other POSes. Becomes available once you provide partner API credentials.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Generic catch-all */}
      <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md p-4 text-sm flex items-start gap-3" data-testid="pos-other-callout">
        <Globe className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Using a different POS?</strong> Our AI extractor is format-agnostic. Just upload your daily sales report or item-wise sales Excel/CSV to the Files tab — we'll detect the layout and import. If anything goes sideways, click "Reprocess" on the file row after teaching the AI a column mapping.
        </div>
      </div>
    </div>
  );
};

const Step = ({ n, icon, title, children }) => (
  <div className="bg-white border border-border rounded-md p-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold">{n}</span>
      <span className="text-primary">{icon}</span>
      <span className="font-semibold text-sm">{title}</span>
    </div>
    <p className="text-xs text-text-secondary leading-relaxed">{children}</p>
  </div>
);

const PosCard = ({ pos }) => (
  <div className="bg-white border border-border rounded-md p-4 hover:shadow-md transition-shadow" data-testid={`pos-card-${pos.key}`}>
    <div className={`inline-block px-2.5 py-1 rounded-md text-[10px] uppercase tracking-wider font-bold border ${pos.color} mb-3`}>
      {pos.region}
    </div>
    <h3 className="text-lg font-semibold text-primary mb-1">{pos.name}</h3>
    <p className="text-[11px] text-text-muted mb-3">Reports to export:</p>
    <ul className="space-y-2">
      {pos.reports.map((r, i) => (
        <li key={i} className="text-xs">
          <div className="flex items-center justify-between gap-2 font-semibold text-text-primary">
            <span>{r.name}</span>
            <span className="text-[10px] text-emerald-700 font-mono whitespace-nowrap"><ArrowRight className="h-3 w-3 inline" /> {r.maps}</span>
          </div>
          <p className="text-[11px] text-text-secondary mt-0.5 leading-snug">{r.path}</p>
        </li>
      ))}
    </ul>
    <Link
      to="/files"
      className="block mt-4 text-center bg-primary text-white py-1.5 rounded text-xs font-semibold hover:bg-primary-hover transition-colors"
      data-testid={`pos-upload-${pos.key}`}
    >
      <Upload className="h-3.5 w-3.5 inline-block mr-1" /> Upload {pos.name} report
    </Link>
  </div>
);

export default POSIntegrations;

// ============== Test Parse Widget ==============
// Lets the user dry-run a POS export against our AI extractor BEFORE persisting it.
// Posts to /api/pos/test-parse — server returns detected module + first ~8 rows.
const TestParseWidget = () => {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    setError('');
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  const runTest = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/pos/test-parse`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      setResult(resp.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Test parse failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-md p-5" data-testid="pos-test-parse-widget">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-primary flex items-center gap-2">
            <FlaskConical className="h-5 w-5" /> Test parse a POS export
          </h2>
          <p className="text-xs text-text-secondary mt-1">
            Drop any POS report here to see what our AI detects — module, confidence, and a preview of normalised rows.{' '}
            <strong>Nothing is saved.</strong> Use this to sanity-check a new POS export format before committing it via Files.
          </p>
        </div>
        {(file || result || error) && (
          <button
            onClick={reset}
            className="text-text-muted hover:text-text-primary p-1"
            data-testid="pos-test-parse-reset"
            aria-label="Reset"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropzone */}
      {!file && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer border-2 border-dashed rounded-md p-8 text-center transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary hover:bg-background-secondary'
          }`}
          data-testid="pos-test-parse-dropzone"
        >
          <Upload className="h-7 w-7 mx-auto text-text-muted mb-2" />
          <p className="text-sm font-medium text-text-primary">Drop a POS export here</p>
          <p className="text-[11px] text-text-muted mt-1">PDF · Excel · CSV · PNG · JPG (max 10MB)</p>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.txt"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="pos-test-parse-input"
          />
        </div>
      )}

      {/* Selected file + run button */}
      {file && !result && !loading && (
        <div className="flex items-center justify-between gap-3 bg-background-secondary border border-border rounded-md p-3" data-testid="pos-test-parse-selected">
          <div className="flex items-center gap-2 min-w-0">
            <FileSpreadsheet className="h-4 w-4 text-primary flex-shrink-0" />
            <span className="text-sm font-medium truncate">{file.name}</span>
            <span className="text-[11px] text-text-muted whitespace-nowrap">({(file.size / 1024).toFixed(1)} KB)</span>
          </div>
          <Button onClick={runTest} size="sm" data-testid="pos-test-parse-run">
            <Sparkles className="h-3.5 w-3.5 mr-1" /> Run AI parse
          </Button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center gap-3 text-sm text-text-secondary py-6 justify-center" data-testid="pos-test-parse-loading">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          Running classifier + extractor… (Gemini 3 Flash)
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="mt-3 bg-rose-50 border border-rose-200 text-rose-900 rounded-md p-3 text-xs flex items-start gap-2" data-testid="pos-test-parse-error">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Result */}
      {result && !loading && (
        <div className="mt-3 space-y-3" data-testid="pos-test-parse-result">
          {/* Summary chips */}
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {result.success ? (
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-900 border border-emerald-200 px-2 py-1 rounded font-semibold">
                <CheckCircle2 className="h-3.5 w-3.5" /> Parsed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-900 border border-rose-200 px-2 py-1 rounded font-semibold">
                <AlertCircle className="h-3.5 w-3.5" /> Could not auto-detect
              </span>
            )}
            {result.detected_module && (
              <span className="bg-primary/10 text-primary border border-primary/20 px-2 py-1 rounded font-semibold" data-testid="pos-test-parse-module">
                Module: {MODULE_LABELS[result.detected_module] || result.detected_module}
              </span>
            )}
            {result.detection_confidence != null && (
              <span className="bg-slate-50 text-slate-700 border border-slate-200 px-2 py-1 rounded">
                Routing conf: <strong>{result.detection_confidence}%</strong>
              </span>
            )}
            {result.overall_confidence != null && (
              <span className="bg-slate-50 text-slate-700 border border-slate-200 px-2 py-1 rounded">
                Extract conf: <strong>{result.overall_confidence}%</strong>
              </span>
            )}
            {result.rows_count != null && (
              <span className="bg-slate-50 text-slate-700 border border-slate-200 px-2 py-1 rounded">
                Rows: <strong>{result.rows_count}</strong>
              </span>
            )}
            {result.model_used && (
              <span className="bg-slate-50 text-slate-500 border border-slate-200 px-2 py-1 rounded font-mono text-[10px]">
                {result.model_used}
              </span>
            )}
          </div>

          {result.detection_reason && (
            <p className="text-[11px] text-text-muted italic">Why: {result.detection_reason}</p>
          )}

          {result.notes && (
            <p className="text-[11px] text-text-secondary bg-amber-50 border border-amber-200 rounded p-2">
              <strong>AI notes:</strong> {result.notes}
            </p>
          )}

          {Array.isArray(result.issues_detected) && result.issues_detected.length > 0 && (
            <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2">
              <strong className="text-amber-900">Issues flagged:</strong>
              <ul className="list-disc list-inside mt-1 text-amber-900">
                {result.issues_detected.slice(0, 5).map((it, i) => <li key={i}>{it}</li>)}
              </ul>
            </div>
          )}

          {/* Preview table */}
          {Array.isArray(result.preview_rows) && result.preview_rows.length > 0 && (
            <div className="border border-border rounded-md overflow-hidden" data-testid="pos-test-parse-preview-table">
              <div className="px-3 py-2 bg-background-secondary text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
                Preview · first {result.preview_rows.length} of {result.rows_count} rows
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-background-secondary text-text-secondary">
                    <tr>
                      {Object.keys(result.preview_rows[0]).filter(k => !k.startsWith('_')).map(k => (
                        <th key={k} className="text-left px-3 py-1.5 font-semibold border-b border-border">{k}</th>
                      ))}
                      <th className="text-left px-3 py-1.5 font-semibold border-b border-border">conf</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.preview_rows.map((row, i) => (
                      <tr key={i} className="border-b border-border last:border-b-0">
                        {Object.keys(result.preview_rows[0]).filter(k => !k.startsWith('_')).map(k => (
                          <td key={k} className="px-3 py-1.5 text-text-primary whitespace-nowrap max-w-[200px] truncate" title={String(row[k] ?? '')}>
                            {row[k] == null || row[k] === '' ? <span className="text-text-muted">—</span> : String(row[k])}
                          </td>
                        ))}
                        <td className="px-3 py-1.5">
                          <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${
                            (row._confidence ?? 0) >= 80 ? 'bg-emerald-100 text-emerald-800' :
                            (row._confidence ?? 0) >= 60 ? 'bg-amber-100 text-amber-800' :
                            'bg-rose-100 text-rose-800'
                          }`}>
                            {row._confidence ?? 0}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.success && (
            <div className="text-[11px] text-text-secondary flex items-center gap-2">
              Looks right? <Link to="/files" className="text-primary font-semibold hover:underline">Upload it for real via Files →</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
